import { generateClient } from 'aws-amplify/api'
import {
  listLeads as listLeadsQuery,
  listBulkLeadUploads as listBulkLeadUploadsQuery,
  getLeadShard as getLeadShardQuery,
  getLeadHistory as getLeadHistoryQuery,
  getLeadBehaviorProfile as getLeadBehaviorProfileQuery,
  getLeadCallHistory as getLeadCallHistoryQuery,
  suggestLeadColumnMapping as suggestLeadColumnMappingQuery,
} from '@/graphql/queries'
import {
  createLead as createLeadMutation,
  editLead as editLeadMutation,
  deleteLead as deleteLeadMutation,
  disableLead as disableLeadMutation,
  enableLead as enableLeadMutation,
  updateLeadStatus as updateLeadStatusMutation,
  getBulkLeadUploadUrl as getBulkLeadUploadUrlMutation,
  deleteBulkUploadLeads as deleteBulkUploadLeadsMutation,
  bulkDeleteLeads as bulkDeleteLeadsMutation,
  bulkUpdateLeadStatus as bulkUpdateLeadStatusMutation,
  generateLeadComments as generateLeadCommentsMutation,
} from '@/graphql/mutations'
import { parseAwsJson } from '@/utils/graphql'
import { getApiMessage, isApiSuccess } from '@/mixins/Extras/ApiResponse'
import { resolveLeadShardItems } from '@/utils/leadShards'

const client = generateClient()

async function runLeadsField(document, field, input) {
  try {
    const variables = input == null ? undefined : { input }
    const { data, errors } = await client.graphql({
      query: document,
      ...(variables ? { variables } : {}),
    })

    if (errors?.length) {
      throw new Error(errors[0].message)
    }

    return parseAwsJson(data?.[field])
  } catch (error) {
    return {
      status: 'ERROR',
      message: getApiMessage(error, 'Request failed'),
    }
  }
}

function optionalString(value) {
  const text = String(value ?? '').trim()
  return text || undefined
}

function optionalStringList(value) {
  const list = (Array.isArray(value) ? value : [])
    .map((item) => String(item ?? '').trim())
    .filter(Boolean)
  return list.length ? list : undefined
}

function cleanCustomFields(value) {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return undefined

  const result = {}
  for (const [key, fieldValue] of Object.entries(value)) {
    const name = String(key ?? '').trim()
    if (!name || fieldValue == null) continue
    const text = typeof fieldValue === 'string' ? fieldValue.trim() : fieldValue
    if (text === '') continue
    result[name] = text
  }
  return Object.keys(result).length ? result : undefined
}

/**
 * Lead attributes travel inside the `fields: AWSJSON` blob
 * (createLeadInput / editLeadInput). Everything is flat — core fields and
 * custom fields alike, keyed exactly as in custom_field_definitions.
 * org_name is never sent; the server derives it from the caller's org.
 */
function buildLeadFields(input = {}, { excludeComments = false } = {}) {
  const fields = {}

  const contactName = optionalString(input.contact_name ?? input.contactName)
  const contactEmail = optionalString(input.contact_email ?? input.contactEmail)
  const contactNumber = optionalString(input.contact_number ?? input.contactNumber)
  const typeOfBusiness = optionalString(input.type_of_business ?? input.typeOfBusiness)
  const industry = optionalString(input.industry)
  const message = optionalString(input.message)
  const comments = optionalString(input.comments)

  if (contactName) fields.contact_name = contactName
  if (contactEmail) fields.contact_email = contactEmail
  if (contactNumber) fields.contact_number = contactNumber
  if (typeOfBusiness) fields.type_of_business = typeOfBusiness
  if (industry) fields.industry = industry
  if (message) fields.message = message
  // editLead silently ignores comments — only settable via updateLeadStatus.
  if (comments && !excludeComments) fields.comments = comments

  const customFields = cleanCustomFields(
    input.custom_fields ?? input.customFields,
  )
  if (customFields) {
    for (const [key, value] of Object.entries(customFields)) {
      if (!(key in fields)) fields[key] = value
    }
  }

  return fields
}

export function fetchLeads(input = {}) {
  const payload = {}

  const startDate = optionalString(input.start_date ?? input.startDate)
  const endDate = optionalString(input.end_date ?? input.endDate)
  const requestStatus = optionalString(input.request_status ?? input.requestStatus)
  const contextName = optionalString(input.context_name ?? input.contextName)
  const contextId = optionalString(input.context_id ?? input.contextId)
  const uploadId = optionalString(input.upload_id ?? input.uploadId)
  const nextToken = optionalString(input.next_token ?? input.nextToken)
  const limit = Number(input.limit)

  if (startDate) payload.start_date = startDate
  if (endDate) payload.end_date = endDate
  if (requestStatus) payload.request_status = requestStatus.toUpperCase()
  if (contextName) payload.context_name = contextName
  if (contextId) payload.context_id = contextId
  if (uploadId) payload.upload_id = uploadId
  if (Number.isFinite(limit) && limit > 0) payload.limit = limit
  if (nextToken) payload.next_token = nextToken

  return runLeadsField(listLeadsQuery, 'listLeads', payload)
}

/**
 * Fetch one shard of a context's lead cache.
 *
 * GraphQL now returns S3 coordinates only:
 *   { bucket, shard_key, shard_index, total_shards }
 * This helper downloads the shard via Amplify Storage and returns
 *   { status, data: { items, shard_index, total_shards, bucket, shard_key } }
 * Legacy inline `{ items }` responses are still accepted.
 */
export async function getLeadShard(input = {}) {
  const payload = {
    context_id: String(input.context_id ?? input.contextId ?? '').trim(),
  }
  const shardIndex = Number(input.shard_index ?? input.shardIndex)
  if (Number.isFinite(shardIndex) && shardIndex >= 0) {
    payload.shard_index = shardIndex
  }

  const response = await runLeadsField(getLeadShardQuery, 'getLeadShard', payload)
  if (!isApiSuccess(response)) return response

  try {
    const data = response?.data ?? response
    const { items, meta } = await resolveLeadShardItems(data)
    return {
      status: 'SUCCESS',
      message: response?.message,
      data: {
        items,
        shard_index: meta.shard_index,
        total_shards: meta.total_shards,
        bucket: meta.bucket,
        shard_key: meta.shard_key,
      },
    }
  } catch (error) {
    return {
      status: 'ERROR',
      message: getApiMessage(error, 'Failed to download lead shard from S3'),
    }
  }
}

/** Full audit trail of status/comment submissions for a lead (request_id).
 * Entries appear only when someone submits via updateMyTaskLeadStatus (or AI
 * post-call update). Independent of call recordings.
 */
export function getLeadHistory(input = {}) {
  const taskId = String(input.task_id ?? input.taskId ?? input.campaignId ?? '').trim()
  return runLeadsField(getLeadHistoryQuery, 'getLeadHistory', {
    request_id: String(input.request_id ?? input.requestId ?? '').trim(),
    // Scope to one campaign when we know which one we are looking at. Omitted
    // elsewhere (the Leads page) so that view keeps showing the whole story.
    ...(taskId ? { task_id: taskId } : {}),
  })
}

/**
 * Per-org, cross-campaign behavior profile for this lead's own phone number
 * (communication style + relationship history) -- the same profile the AI
 * reads at call setup, so a human agent can read it before dialing too.
 * Returns { behavior_profile: string|null }.
 */
export function getLeadBehaviorProfile(input = {}) {
  return runLeadsField(getLeadBehaviorProfileQuery, 'getLeadBehaviorProfile', {
    request_id: String(input.request_id ?? input.requestId ?? '').trim(),
  })
}

/**
 * Softphone / AI-dialer CALLS for a lead — recordings + transcripts.
 * Captured automatically when a call finishes, even if remarks were never
 * submitted. Pass task_id to scope to one campaign; omit for all campaigns.
 * Join to lead history via call_uuid when the client passed it on status save.
 */
export function getLeadCallHistory(input = {}) {
  const payload = {
    request_id: String(input.request_id ?? input.requestId ?? '').trim(),
  }
  const taskId = String(
    input.task_id ?? input.taskId ?? input.campaign_id ?? input.campaignId ?? '',
  ).trim()
  if (taskId) payload.task_id = taskId

  return runLeadsField(getLeadCallHistoryQuery, 'getLeadCallHistory', payload)
}

/**
 * Suggest comment / status / score from a completed softphone transcript.
 * Does not write — agent reviews then submits via updateMyTaskLeadStatus.
 */
export function generateLeadComments(input = {}) {
  return runLeadsField(generateLeadCommentsMutation, 'generateLeadComments', {
    request_id: String(input.request_id ?? input.requestId ?? '').trim(),
    call_uuid: String(input.call_uuid ?? input.callUuid ?? '').trim(),
  })
}

export function createLead(input = {}) {
  const payload = {
    fields: JSON.stringify(buildLeadFields(input)),
  }

  const contextNames = optionalStringList(
    input.context_names ?? input.contextNames,
  )
  if (contextNames) payload.context_names = contextNames

  const taskId = String(input.task_id ?? input.taskId ?? '').trim()
  if (taskId) payload.task_id = taskId

  return runLeadsField(createLeadMutation, 'createLead', payload)
}

export function editLead(input = {}) {
  const payload = {
    request_id: String(input.request_id ?? input.requestId ?? '').trim(),
  }

  // Three distinct behaviors: omit → context untouched; explicit null →
  // clear the context (unassigned lead); a name → move to that context.
  if (input.context_name === null || input.contextName === null) {
    payload.context_name = null
  } else {
    const contextName = optionalString(input.context_name ?? input.contextName)
    if (contextName) payload.context_name = contextName
  }

  const requestStatus = optionalString(input.request_status ?? input.requestStatus)
  if (requestStatus) payload.request_status = requestStatus.toUpperCase()

  const fields = buildLeadFields(input, { excludeComments: true })
  if (Object.keys(fields).length) payload.fields = JSON.stringify(fields)

  // Per-lead override of a RECURRING_REMINDER campaign's cadence -- null
  // clears it (back to the campaign's own default), undefined leaves it
  // untouched. Distinguish via hasOwnProperty since null is a meaningful
  // value here, unlike the optionalString fields above.
  if (Object.prototype.hasOwnProperty.call(input, 'recurrence_interval_days')) {
    payload.recurrence_interval_days =
      input.recurrence_interval_days == null ? null : Number(input.recurrence_interval_days)
  }

  return runLeadsField(editLeadMutation, 'editLead', payload)
}

/**
 * Request a presigned URL for a bulk lead CSV upload.
 * Returns {upload_id, upload_url, s3_key, expires_in} in data.
 */
export function getBulkLeadUploadUrl(input = {}) {
  const payload = {
    context_names: optionalStringList(input.context_names ?? input.contextNames) ?? [],
    filename: String(input.filename ?? '').trim(),
  }
  const forceTaskId = optionalString(input.force_task_id ?? input.forceTaskId)
  if (forceTaskId) payload.force_task_id = forceTaskId
  return runLeadsField(getBulkLeadUploadUrlMutation, 'getBulkLeadUploadUrl', payload)
}

/**
 * AI fallback for bulk lead upload's column mapping -- only called for
 * headers the deterministic alias/exact-match pass left unmatched. Returns
 * { mapping: { [headerIndex]: fieldName | null } } in data.
 */
export function suggestLeadColumnMapping(input = {}) {
  return runLeadsField(suggestLeadColumnMappingQuery, 'suggestLeadColumnMapping', {
    headers: Array.isArray(input.headers) ? input.headers.map((h) => String(h ?? '')) : [],
    target_fields: Array.isArray(input.target_fields ?? input.targetFields)
      ? (input.target_fields ?? input.targetFields).map((f) => ({
          name: String(f?.name ?? ''),
          label: String(f?.label ?? ''),
        }))
      : [],
  })
}

export function listBulkLeadUploads(input = {}) {
  const payload = {}

  const contextId = optionalString(input.context_id ?? input.contextId)
  if (contextId) payload.context_id = contextId

  const limit = Number(input.limit)
  if (Number.isFinite(limit) && limit > 0) payload.limit = limit

  const nextToken = optionalString(input.next_token ?? input.nextToken)
  if (nextToken) payload.next_token = nextToken

  return runLeadsField(listBulkLeadUploadsQuery, 'listBulkLeadUploads', payload)
}

export function deleteLead(input = {}) {
  return runLeadsField(deleteLeadMutation, 'deleteLead', {
    request_id: String(input.request_id ?? input.requestId ?? '').trim(),
  })
}

export function disableLead(input = {}) {
  return runLeadsField(disableLeadMutation, 'disableLead', {
    request_id: String(input.request_id ?? input.requestId ?? '').trim(),
  })
}

export function enableLead(input = {}) {
  return runLeadsField(enableLeadMutation, 'enableLead', {
    request_id: String(input.request_id ?? input.requestId ?? '').trim(),
  })
}

export function updateLeadStatus(input = {}) {
  const payload = {
    request_id: String(input.request_id ?? input.requestId ?? '').trim(),
    request_status: String(input.request_status ?? input.requestStatus ?? '')
      .trim()
      .toUpperCase(),
    comments: String(input.comments ?? '').trim(),
  }
  const callUuid = optionalString(input.call_uuid ?? input.callUuid)
  if (callUuid) payload.call_uuid = callUuid
  return runLeadsField(updateLeadStatusMutation, 'updateLeadStatus', payload)
}

/**
 * Undo an entire bulk upload — deletes every lead currently carrying this
 * upload_id. Irreversible; confirm with the user first.
 * data: { deleted_count }
 */
export function deleteBulkUploadLeads(input = {}) {
  return runLeadsField(deleteBulkUploadLeadsMutation, 'deleteBulkUploadLeads', {
    upload_id: String(input.upload_id ?? input.uploadId ?? '').trim(),
  })
}

/**
 * Multi-select delete. Irreversible; confirm with the user first.
 * data: { deleted_count, errors: [{request_id, error}] }
 */
export function bulkDeleteLeads(input = {}) {
  return runLeadsField(bulkDeleteLeadsMutation, 'bulkDeleteLeads', {
    request_ids: optionalStringList(input.request_ids ?? input.requestIds) ?? [],
  })
}

/**
 * Multi-select status change with mandatory comment.
 * data: { updated_count, errors: [{request_id, error}] }
 */
export function bulkUpdateLeadStatus(input = {}) {
  const payload = {
    request_ids: optionalStringList(input.request_ids ?? input.requestIds) ?? [],
    request_status: String(input.request_status ?? input.requestStatus ?? '')
      .trim()
      .toUpperCase(),
    comments: String(input.comments ?? '').trim(),
  }
  const callUuid = optionalString(input.call_uuid ?? input.callUuid)
  if (callUuid) payload.call_uuid = callUuid
  return runLeadsField(bulkUpdateLeadStatusMutation, 'bulkUpdateLeadStatus', payload)
}
